import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { AlertTriangle, ChevronDown, ChevronRight } from 'lucide-react';
import type { AccountCoverage } from '../../lib/publishing/multiAccount';
import type { PostDestinationId } from '../../lib/publishing/destinations';
import {
  buildPerAccountPreview,
  canPreviewEntry,
  type ContextsLoad,
} from '../../lib/publishing/perAccountPreview';
import { Button } from '../../components/ui/button';
import { getWildcardContexts } from './api';
import { DestinationChip } from './CoveragePanel';
import { PerAccountPreviewDialog } from './PerAccountPreviewDialog';
import type { ComposerState } from './composerState';

// Post to Many — the per-account preview LIST (round 1b item 7). A list, not a
// stepper: the check is for ONE account rendering oddly, and a list can be
// scanned. Every rule (per-account resolution, failed rows, blank fields, which
// captions apply) is the pure buildPerAccountPreview, tested there.
//
// ⚠️ ON OPEN ONLY. The contexts are fetched for the SELECTION, in one request,
// only while the list is open — ticking accounts with it closed costs nothing.
// (Why a second read rather than widening the account list: carry it on the list
// when every row needs it; fetch it for the selection when only the selection
// needs it. See the /wildcard-contexts route.)

export function PerAccountPreviewList({
  coverage,
  baseCaption,
  ownCaptions,
  state,
}: {
  coverage: AccountCoverage[];
  baseCaption: string;
  ownCaptions: Array<{ destination: PostDestinationId; text: string }>;
  // The SHARED composition, for a row's Preview dialog. Read-only here: the
  // dialog narrows a copy per dealer and never writes back.
  state: ComposerState;
}) {
  const [open, setOpen] = useState(false);
  const [previewing, setPreviewing] = useState<string | null>(null);
  const ids = coverage.map((c) => c.account.id);
  const key = [...ids].sort();
  const contexts = useQuery({
    queryKey: ['publishing-wildcard-contexts', key],
    queryFn: () => getWildcardContexts(ids),
    enabled: open && ids.length > 0,
    staleTime: 5 * 60 * 1000,
    retry: false,
  });

  if (ids.length === 0) return null;

  const load: ContextsLoad = contexts.isError
    ? {
        status: 'error',
        message:
          contexts.error instanceof Error
            ? contexts.error.message
            : "Couldn't load the accounts' details.",
      }
    : contexts.data
      ? { status: 'ok', contexts: contexts.data }
      : { status: 'loading' };
  const entries = open ? buildPerAccountPreview({ coverage, load, baseCaption, ownCaptions }) : [];
  const flagged = entries.filter(
    (e) => e.state === 'failed' || (e.state === 'ok' && e.captions.some((c) => c.blankFields.length > 0)),
  ).length;

  return (
    <div className="rounded-lg border border-slate-200">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm font-medium text-slate-700 hover:bg-slate-50"
      >
        {open ? <ChevronDown className="h-4 w-4 text-slate-400" /> : <ChevronRight className="h-4 w-4 text-slate-400" />}
        Preview each account ({ids.length})
        {open && flagged > 0 && (
          <span className="ml-auto rounded bg-amber-100 px-1.5 py-0.5 text-[10px] font-medium text-amber-800">
            {flagged} to check
          </span>
        )}
      </button>
      {open && (
        <ul className="max-h-[70vh] divide-y divide-slate-100 overflow-y-auto border-t border-slate-100 scroll-subtle">
          {entries.map((e) => (
            <li key={e.accountId} className="space-y-1.5 px-3 py-2.5">
              <div className="flex flex-wrap items-center gap-1.5">
                <span className="text-sm font-medium text-slate-900">{e.name}</span>
                {canPreviewEntry(e) && (
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    className="order-last ml-auto h-6 px-2 text-xs"
                    onClick={() => setPreviewing(e.accountId)}
                  >
                    Preview
                  </Button>
                )}
                {e.coverage.covered.map((d) => (
                  <DestinationChip key={d} dest={d} />
                ))}
                {e.coverage.skipped.map((d) => (
                  <DestinationChip key={d} dest={d} muted />
                ))}
              </div>
              {e.state === 'loading' && <p className="text-xs text-slate-400">Loading…</p>}
              {e.state === 'failed' && (
                <p className="flex items-start gap-1.5 text-xs text-red-700">
                  <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                  {e.reason}
                </p>
              )}
              {e.state === 'ok' && (
                <>
                  {e.note && <p className="text-xs text-slate-500">{e.note}</p>}
                  {e.captions.map((c) => (
                    <div key={c.label}>
                      {e.captions.length > 1 && (
                        <p className="text-[11px] font-medium uppercase tracking-wide text-slate-500">{c.label}</p>
                      )}
                      <p className="whitespace-pre-wrap text-sm text-slate-800">{c.text}</p>
                      {c.blankFields.length > 0 && (
                        <p className="mt-0.5 text-xs text-amber-800">
                          Empty for this account: {c.blankFields.join(', ')}
                        </p>
                      )}
                    </div>
                  ))}
                </>
              )}
            </li>
          ))}
        </ul>
      )}
      {(() => {
        // Only an ok row can open (canPreviewEntry), so its context is loaded.
        const entry = previewing ? entries.find((x) => x.accountId === previewing) : undefined;
        const context = previewing ? contexts.data?.[previewing] : undefined;
        if (!entry || !context) return null;
        return (
          <PerAccountPreviewDialog
            open
            onOpenChange={(o) => {
              if (!o) setPreviewing(null);
            }}
            accountName={entry.name}
            coverage={entry.coverage}
            context={context}
            state={state}
          />
        );
      })()}
    </div>
  );
}
